import React, { createContext, useState, useContext, useEffect } from "react";
import { authAPI } from "../api/auth";
import toast from "react-hot-toast";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Role hierarchy for permission checking
const ROLE_HIERARCHY = {
  super_admin: 5,
  project_manager: 4,
  sub_manager: 3,
  foreman: 2,
  worker: 1,
};

// Permission definitions
const PERMISSIONS = {
  // Admin only
  manage_users: ["super_admin"],
  manage_settings: ["super_admin", "project_manager"],
  view_all_projects: ["super_admin", "project_manager"],

  // Project management
  create_project: ["super_admin", "project_manager"],
  edit_project: ["super_admin", "project_manager"],
  delete_project: ["super_admin"],

  // Task management
  create_task: ["super_admin", "project_manager", "sub_manager"],
  edit_task: ["super_admin", "project_manager", "sub_manager"],
  delete_task: ["super_admin", "project_manager"],
  assign_task: ["super_admin", "project_manager", "sub_manager"],

  // Inventory
  manage_inventory: ["super_admin", "project_manager"],
  view_inventory: ["super_admin", "project_manager", "sub_manager"],

  // Labor
  manage_workers: ["super_admin", "project_manager", "sub_manager"],
  view_workers: ["super_admin", "project_manager", "sub_manager", "foreman"],
  clock_in_out: [
    "super_admin",
    "project_manager",
    "sub_manager",
    "foreman",
    "worker",
  ],

  // Reports
  view_reports: ["super_admin", "project_manager"],
  export_reports: ["super_admin", "project_manager"],
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      if (response.success) {
        setUser(response.data);
        setIsAuthenticated(true);
      }
    } catch (error) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      if (response.success) {
        const { accessToken, refreshToken, user } = response.data;
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("user", JSON.stringify(user));
        setUser(user);
        setIsAuthenticated(true);
        toast.success(`Welcome back, ${user.full_name}!`);
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.error?.message || "Login failed";
      toast.error(message);
      return { success: false, message };
    }
  };

  const register = async (data) => {
    try {
      const response = await authAPI.register(data);
      if (response.success) {
        toast.success("Registration successful! Please login.");
        return { success: true };
      }
    } catch (error) {
      const message =
        error.response?.data?.error?.message || "Registration failed";
      toast.error(message);
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      setUser(null);
      setIsAuthenticated(false);
      toast.success("Logged out successfully");
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
  };

  // Role checking functions
  const hasRole = (role) => {
    if (!user) return false;
    return user.role === role;
  };

  const hasAnyRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    const allowedRoles = PERMISSIONS[permission];
    if (!allowedRoles) return false;
    return allowedRoles.includes(user.role);
  };

  const isAtLeast = (role) => {
    if (!user) return false;
    const userLevel = ROLE_HIERARCHY[user.role] || 0;
    const requiredLevel = ROLE_HIERARCHY[role] || 0;
    return userLevel >= requiredLevel;
  };

  const getRoleDisplayName = (role) => {
    const names = {
      super_admin: "Super Admin",
      project_manager: "Project Manager",
      sub_manager: "Sub Manager",
      foreman: "Foreman",
      worker: "Worker",
    };
    return names[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      super_admin: "bg-purple-500",
      project_manager: "bg-blue-500",
      sub_manager: "bg-green-500",
      foreman: "bg-orange-500",
      worker: "bg-slate-500",
    };
    return colors[role] || "bg-slate-500";
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    loadUser,
    // Role utilities
    hasRole,
    hasAnyRole,
    hasPermission,
    isAtLeast,
    getRoleDisplayName,
    getRoleColor,
    ROLE_HIERARCHY,
    PERMISSIONS,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
